export const STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION = 1;

export class StudioPublicationDescriptorError extends Error {
  constructor(message, { code = "STUDIO_PUBLICATION_DESCRIPTOR_ERROR", details = {} } = {}) {
    super(message);
    this.name = "StudioPublicationDescriptorError";
    this.code = code;
    this.details = isPlainObject(details) ? cloneJsonValue(details) : {};
  }
}

export function createStudioPublicationDescriptor(input = {}) {
  assertPlainObject(input, "descriptor");

  const descriptor = {
    schemaVersion: normalizePositiveInteger(
      input.schemaVersion ?? STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION,
      "schemaVersion"
    ),
    id: normalizeRequiredString(input.id, "id"),
    studioId: normalizeRequiredString(input.studioId, "studioId"),
    domainId: normalizeRequiredString(input.domainId, "domainId"),
    companyUuid: normalizeRequiredString(input.companyUuid, "companyUuid"),
    activityUuid: normalizeRequiredString(input.activityUuid, "activityUuid"),
    documentId: normalizeRequiredString(input.documentId, "documentId"),
    versionId: normalizeRequiredString(input.versionId, "versionId"),
    publicationId: normalizeRequiredString(input.publicationId, "publicationId"),
    publishedAt: normalizeIsoTimestamp(input.publishedAt, "publishedAt"),
    runtimeVersion: normalizePositiveInteger(
      input.runtimeVersion,
      "runtimeVersion"
    ),
    payload: normalizePlainObject(input.payload, "payload"),
    metadata: normalizePlainObject(input.metadata ?? {}, "metadata"),
  };

  return deepFreeze(descriptor);
}

export function validateStudioPublicationDescriptor(value) {
  const issues = [];

  if (!isPlainObject(value)) {
    return {
      valid: false,
      issues: [
        {
          field: "descriptor",
          code: "INVALID_DESCRIPTOR",
          message: "Studio publication descriptor must be a plain object.",
        },
      ],
    };
  }

  validateRequiredString(value.id, "id", issues);
  validateRequiredString(value.studioId, "studioId", issues);
  validateRequiredString(value.domainId, "domainId", issues);
  validateRequiredString(value.companyUuid, "companyUuid", issues);
  validateRequiredString(value.activityUuid, "activityUuid", issues);
  validateRequiredString(value.documentId, "documentId", issues);
  validateRequiredString(value.versionId, "versionId", issues);
  validateRequiredString(value.publicationId, "publicationId", issues);

  validatePositiveInteger(value.schemaVersion, "schemaVersion", issues);
  validatePositiveInteger(value.runtimeVersion, "runtimeVersion", issues);

  if (!isValidIsoTimestamp(value.publishedAt)) {
    issues.push({
      field: "publishedAt",
      code: "INVALID_PUBLISHED_AT",
      message: "publishedAt must be a valid ISO-8601 timestamp.",
    });
  }

  if (!isPlainObject(value.payload)) {
    issues.push({
      field: "payload",
      code: "INVALID_PAYLOAD",
      message: "payload must be a plain object.",
    });
  } else if (!isJsonCompatible(value.payload)) {
    issues.push({
      field: "payload",
      code: "INVALID_PAYLOAD",
      message: "payload must be JSON-compatible.",
    });
  }

  if (!isPlainObject(value.metadata)) {
    issues.push({
      field: "metadata",
      code: "INVALID_METADATA",
      message: "metadata must be a plain object.",
    });
  } else if (!isJsonCompatible(value.metadata)) {
    issues.push({
      field: "metadata",
      code: "INVALID_METADATA",
      message: "metadata must be JSON-compatible.",
    });
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}

export function assertStudioPublicationDescriptor(value) {
  const result = validateStudioPublicationDescriptor(value);

  if (!result.valid) {
    throw new StudioPublicationDescriptorError(
      "Invalid Studio publication descriptor.",
      {
        code: "INVALID_STUDIO_PUBLICATION_DESCRIPTOR",
        details: {
          issues: result.issues,
        },
      }
    );
  }

  return value;
}

export function isStudioPublicationDescriptor(value) {
  return validateStudioPublicationDescriptor(value).valid;
}

export function cloneStudioPublicationDescriptor(value) {
  assertStudioPublicationDescriptor(value);

  return createStudioPublicationDescriptor({
    ...value,
    payload: cloneJsonValue(value.payload),
    metadata: cloneJsonValue(value.metadata),
  });
}

function normalizeRequiredString(value, field) {
  if (typeof value !== "string" || !value.trim()) {
    throw new StudioPublicationDescriptorError(
      `${field} must be a non-empty string.`,
      {
        code: `INVALID_${toErrorCode(field)}`,
        details: { field },
      }
    );
  }

  return value.trim();
}

function normalizePositiveInteger(value, field) {
  if (!Number.isInteger(value) || value < 1) {
    throw new StudioPublicationDescriptorError(
      `${field} must be a positive integer.`,
      {
        code: `INVALID_${toErrorCode(field)}`,
        details: { field },
      }
    );
  }

  return value;
}

function normalizeIsoTimestamp(value, field) {
  if (!isValidIsoTimestamp(value)) {
    throw new StudioPublicationDescriptorError(
      `${field} must be a valid ISO-8601 timestamp.`,
      {
        code: `INVALID_${toErrorCode(field)}`,
        details: { field },
      }
    );
  }

  return new Date(value).toISOString();
}

function normalizePlainObject(value, field) {
  if (!isPlainObject(value)) {
    throw new StudioPublicationDescriptorError(
      `${field} must be a plain object.`,
      {
        code: `INVALID_${toErrorCode(field)}`,
        details: { field },
      }
    );
  }

  if (!isJsonCompatible(value)) {
    throw new StudioPublicationDescriptorError(
      `${field} must be JSON-compatible.`,
      {
        code: `INVALID_${toErrorCode(field)}`,
        details: { field },
      }
    );
  }

  return cloneJsonValue(value);
}

function validateRequiredString(value, field, issues) {
  if (typeof value !== "string" || !value.trim()) {
    issues.push({
      field,
      code: `INVALID_${toErrorCode(field)}`,
      message: `${field} must be a non-empty string.`,
    });
  }
}

function validatePositiveInteger(value, field, issues) {
  if (!Number.isInteger(value) || value < 1) {
    issues.push({
      field,
      code: `INVALID_${toErrorCode(field)}`,
      message: `${field} must be a positive integer.`,
    });
  }
}

function isValidIsoTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp);
}

function assertPlainObject(value, field) {
  if (!isPlainObject(value)) {
    throw new StudioPublicationDescriptorError(
      `${field} must be a plain object.`,
      {
        code: `INVALID_${toErrorCode(field)}`,
        details: { field },
      }
    );
  }
}

function isPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function cloneJsonValue(value) {
  if (value === undefined) {
    return undefined;
  }

  return JSON.parse(JSON.stringify(value));
}

function isJsonCompatible(value) {
  try {
    JSON.stringify(value);
    return !containsUnsupportedValue(value);
  } catch (_error) {
    return false;
  }
}

function containsUnsupportedValue(value) {
  if (
    typeof value === "undefined" ||
    typeof value === "function" ||
    typeof value === "symbol" ||
    typeof value === "bigint"
  ) {
    return true;
  }

  if (value === null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.some(containsUnsupportedValue);
  }

  if (isPlainObject(value)) {
    return Object.values(value).some(containsUnsupportedValue);
  }

  return typeof value === "object";
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }

  Object.freeze(value);

  for (const nestedValue of Object.values(value)) {
    deepFreeze(nestedValue);
  }

  return value;
}

function toErrorCode(field) {
  return String(field)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .toUpperCase();
}
